import { fetchPerplexityPrompt, fetchChapterGenerationPrompt, fetchPersonalityAnalysis, savePersonalityAnalysis } from './supabase';

const OPENAI_API_KEY = process.env.NEXT_PUBLIC_OPENAI_API_KEY;
const PERPLEXITY_API_KEY = process.env.NEXT_PUBLIC_PERPLEXITY_API_KEY;

export type LessonBlockType = 'text' | 'table' | 'list' | 'timeline' | 'swot' | 'ratio-chart';

export interface LessonBlock {
  type: LessonBlockType;
  data: {
    content?: string | string[];
    headers?: string[];
    rows?: string[][];
    strengths?: string[];
    weaknesses?: string[];
    opportunities?: string[];
    threats?: string[];
    x?: number[];
    y?: number[];
    labels?: string[];
    xLabel?: string;
    yLabel?: string;
  };
}

export interface CourseLesson {
  title: string;
  description: string;
  hookCta?: string;
  metadata: {
    chapter: string;
    order: number;
  };
  blocks?: LessonBlock[];
  items?: LessonBlock[]; // Legacy support
}

export interface GeneratedCourse {
  title: string;
  description?: string;
  chapters: {
    title: string;
    lessons: CourseLesson[];
  }[];
  promptVersion?: string;
}

const TOPIC_KEY_TO_CHAPTER_TITLE: { [key: string]: string } = {
  'understand-business': 'Understand the business',
  'market-tam-competition': 'Market/TAM/competition',
  'basic-financials': 'Basic financials',
  'risk-awareness': 'Risk awareness',
  'moat-competitive-advantage': 'Moat / competitive advantage',
  'management-quality': 'Management quality',
  'financial-depth': 'Financial depth',
  'catalysts': 'Catalysts',
  'valuation': 'Valuation',
  'price-action-technicals': 'Price action / technicals',
};

const GOAL_CHAPTER_ORDER: { [key: string]: string[] } = {
  quick: [
    'Understand the business',
    'Market/TAM/competition',
    'Basic financials',
    'Risk awareness',
  ],
  solid: [
    'Understand the business',
    'Market/TAM/competition',
    'Basic financials',
    'Risk awareness',
    'Moat / competitive advantage',
    'Management quality',
    'Financial depth',
    'Catalysts',
  ],
  deep: [
    'Understand the business',
    'Market/TAM/competition',
    'Basic financials',
    'Risk awareness',
    'Moat / competitive advantage',
    'Management quality',
    'Financial depth',
    'Catalysts',
    'Valuation',
    'Price action / technicals',
  ],
};

function replacePromptVariables(
  template: string,
  variables: { [key: string]: string }
): string {
  let result = template;
  for (const [key, value] of Object.entries(variables)) {
    const regex = new RegExp(`\\$\\{${key}\\}`, 'g');
    result = result.replace(regex, value);
  }
  return result;
}

export function generateCourseSkeleton(params: {
  stockName: string;
  goal: string;
  selectedTopics?: { [key: string]: string[] };
  industryChapters?: Array<{ value: string; label: string }>;
}): {
  title: string;
  chapters: Array<{
    title: string;
    topicKey: string;
    lessons: any[];
  }>;
} {
  const { stockName, goal, selectedTopics, industryChapters } = params;

  let courseTitle: string;
  if (goal === 'deep') {
    courseTitle = `Deep dive into ${stockName}`;
  } else if (goal === 'solid') {
    courseTitle = `Master ${stockName}`;
  } else if (goal === 'quick') {
    courseTitle = `Quick overview of ${stockName}`;
  } else {
    courseTitle = `Learn ${stockName}`;
  }

  const chapters: Array<{
    title: string;
    topicKey: string;
    lessons: any[];
  }> = [];

  let selectedTopicKeys: string[] | null = null;
  if (selectedTopics) {
    selectedTopicKeys = Object.values(selectedTopics).flat();
  }

  if (industryChapters && selectedTopicKeys) {
    selectedTopicKeys.forEach((topicKey) => {
      const industryChapter = industryChapters.find((ch) => ch.value === topicKey);
      if (industryChapter) {
        chapters.push({
          title: industryChapter.label,
          topicKey: industryChapter.value,
          lessons: [],
        });
      }
    });
  } else {
    const orderedChapterTitles = GOAL_CHAPTER_ORDER[goal] || [];

    orderedChapterTitles.forEach((chapterTitle) => {
      const topicKey = Object.keys(TOPIC_KEY_TO_CHAPTER_TITLE).find(
        (key) => TOPIC_KEY_TO_CHAPTER_TITLE[key] === chapterTitle
      );

      if (topicKey) {
        if (!selectedTopicKeys || selectedTopicKeys.includes(topicKey)) {
          chapters.push({
            title: chapterTitle,
            topicKey,
            lessons: [],
          });
        }
      }
    });
  }

  return {
    title: courseTitle,
    chapters,
  };
}

export async function callPerplexityAPI(params: {
  stockName: string;
  ticker: string;
  topicKey: string;
}): Promise<string> {
  const { stockName, ticker, topicKey } = params;

  try {
    const config = await fetchPerplexityPrompt(topicKey);
    const prompt = replacePromptVariables(config.prompt, {
      stockName,
      ticker,
    });

    const requestBody: any = {
      model: 'sonar',
      messages: [
        {
          role: 'user',
          content: prompt,
        },
      ],
      temperature: 0.2,
      max_tokens: 4000,
    };

    if (config.searchDomainFilter && config.searchDomainFilter.length > 0) {
      requestBody.search_domain_filter = config.searchDomainFilter;
    }
    if (config.searchRecencyFilter) {
      requestBody.search_recency_filter = config.searchRecencyFilter;
    }

    const response = await fetch('https://api.perplexity.ai/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${PERPLEXITY_API_KEY}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Perplexity API error (${response.status}): ${errorText || response.statusText}`);
    }

    const data = await response.json();
    const content = data.choices?.[0]?.message?.content?.trim();

    if (!content) {
      throw new Error('Perplexity returned empty data');
    }

    return content;
  } catch (error) {
    console.error('Error calling Perplexity API:', error);
    throw error;
  }
}

export async function generateChapter(params: {
  stockName: string;
  ticker: string;
  chapterTitle: string;
  level: string;
  goal: string;
  perplexityData: string;
}): Promise<{
  lessons: Array<{
    lessonTitle: string;
    lessonDescription?: string;
    blocks: LessonBlock[];
  }>;
}> {
  const { stockName, ticker, chapterTitle, level, goal, perplexityData } = params;

  try {
    const promptTemplate = await fetchChapterGenerationPrompt();
    const finalPrompt = replacePromptVariables(promptTemplate, {
      stockName,
      ticker,
      chapterTitle,
      level,
      goal,
      perplexityData,
    });

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'user',
            content: finalPrompt,
          },
        ],
        temperature: 0.7,
        max_tokens: 16000,
      }),
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.statusText}`);
    }

    const data = await response.json();
    const content = data.choices[0].message.content.trim();

    let jsonContent = content;
    if (content.startsWith('```json')) {
      jsonContent = content.replace(/```json\n?/g, '').replace(/```\n?/g, '');
    }

    const parsed = JSON.parse(jsonContent);

    if (!parsed.lessons || !Array.isArray(parsed.lessons)) {
      throw new Error('Invalid chapter format: missing lessons array');
    }

    return parsed;
  } catch (error) {
    console.error('Error generating chapter:', error);
    throw error;
  }
}

export async function generateStockQuizQuestions(params: {
  ticker: string;
  stockName?: string;
}): Promise<Array<{ question: string; answer: boolean; explanation: string }>> {
  const { ticker, stockName } = params;

  const companyLabel = stockName ? `${stockName} (${ticker})` : ticker;

  const prompt = [
    `Generate EXACTLY 5 true/false questions about ${companyLabel} for starting investors. No more, no less.`,
    `Questions should cover: very interesting, tricky, company history, stock information, business model, market position, recent news, and basic facts.`,
    `Mix of difficulty: 2 medium/hard, 3 challenging.`,
    `Each question should be clear, concise, and factual.`,
    `Include a short explanation (max 15 words) for each question.`,
    `Return ONLY valid JSON in this exact format (exactly 5 items):`,
    `[`,
    `  {`,
    `    "question": "The question text here",`,
    `    "answer": true,`,
    `    "explanation": "Short explanation here (max 15 words)"`,
    `  }`,
    `]`,
    `Make questions engaging and educational and not too easy never. Avoid overly technical jargon.`,
  ].join('\n');

  try {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'user',
            content: prompt,
          },
        ],
        temperature: 0.8,
        max_tokens: 1500,
      }),
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.statusText}`);
    }

    const data = await response.json();
    const content = data.choices?.[0]?.message?.content?.trim();

    if (!content) {
      throw new Error('OpenAI returned empty quiz questions.');
    }

    let jsonContent = content;
    if (content.startsWith('```json')) {
      jsonContent = content.replace(/```json\n?/g, '').replace(/```\n?/g, '');
    } else if (content.startsWith('```')) {
      jsonContent = content.replace(/```\n?/g, '');
    }

    const questions = JSON.parse(jsonContent);

    if (!Array.isArray(questions) || questions.length !== 5) {
      throw new Error('Invalid quiz format: expected array of 5 questions');
    }

    return questions;
  } catch (error) {
    console.error('Error generating stock quiz questions:', error);
    throw error;
  }
}

export async function generateStockPersonalityAnalysis(params: {
  ticker: string;
  stockName: string;
}): Promise<{
  title: string;
  summary: string;
  personalityTraits: string[];
  blindSpots: string;
  mbtiType: string;
  investorType: string;
}> {
  const { ticker, stockName } = params;

  try {
    const cached = await fetchPersonalityAnalysis(ticker);
    if (cached) {
      return cached;
    }
  } catch (error) {
    console.warn('Error fetching cached personality analysis, will generate new one:', error);
  }

  const prompt = [
    `Give me a "personality type" (what if ${stockName} was a person?) analysis for ${stockName} (${ticker}).`,
    `Make it engaging, fun, and brutally honest. Keep it short and bitesized so it fits on one screen.`,
    ``,
    `IMPORTANT — MBTI LADDER CONSTRAINT (MUST FOLLOW):`,
    ``,
    `You MUST assign the company ONE clear MBTI-style personality using the 16-type ladder system.`,
    ``,
    `Use ONLY this framework:`,
    ``,
    `• Analysts (NT): INTJ, INTP, ENTJ, ENTP → logic-driven, strategic, systems-focused, scale-oriented companies`,
    `• Diplomats (NF): INFJ, INFP, ENFJ, ENFP → vision-led, narrative-driven, belief-based, mission-oriented companies`,
    `• Sentinels (SJ): ISTJ, ISFJ, ESTJ, ESFJ → structured, execution-focused, stability-driven, process-oriented companies`,
    `• Explorers (SP): ISTP, ISFP, ESTP, ESFP → momentum-driven, opportunistic, risk-tolerant, speed-focused companies`,
    ``,
    `You MUST:`,
    `1) Select EXACTLY ONE MBTI type (e.g. ENTJ) — no mixes, no hedging`,
    `2) Explicitly map each MBTI letter to real company behavior:`,
    `   - E vs I → loud media dominance vs quiet execution`,
    `   - S vs N → operational realism vs future vision & storytelling`,
    `   - T vs F → cold fundamentals vs emotional belief-driven narrative`,
    `   - J vs P → disciplined execution vs chaotic adaptability`,
    `3) Ensure all personality traits reinforce the chosen MBTI type`,
    `4) Clearly imply what kind of investor is attracted to this MBTI personality`,
    ``,
    `The MBTI choice must influence tone, traits, blind spots, and investor psychology.`,
    `Commit fully, even if the take is controversial.`,
    ``,
    `IMPORTANT: Focus on the COMPANY's character, not just stock metrics. The personality should reflect:`,
    `- Company's volatility: How unpredictable and swingy the business/operations are (not just stock price)`,
    `- Hype: The buzz, excitement, and media attention the company generates`,
    `- Volume: How much activity, attention, and engagement the company commands`,
    `- Branding: The company's public image, reputation, and how people perceive them`,
    ``,
    `TONE: Be savage and brutally honest. Call out what kind of investor buys this company.`,
    `For example: If someone buys Nvidia, they're either hype-driven FOMO buyers chasing the AI wave, or fundamentalists who genuinely believe in the AI transformation.`,
    `Be direct about whether investors are buying the hype, the fundamentals, or both.`,
    ``,
    `Return ONLY valid JSON in this exact format:`,
    `{`,
    `  "title": "${stockName} - [Creative Personality Type Name]",`,
    `  "summary": "[2-3 engaging, savage sentences about the company's personality that incorporates volatility, hype, volume, and branding. Be honest about what drives investors to this company. Must align with the chosen MBTI type.]",`,
    `  "personalityTraits": [`,
    `    "Trait Name: Brief description (must reflect MBTI dimensions)",`,
    `    "Trait Name: Brief description (must reflect MBTI dimensions)",`,
    `    "Trait Name: Brief description (must reflect MBTI dimensions)",`,
    `    "Trait Name: Brief description (must reflect MBTI dimensions)"`,
    `  ],`,
    `  "blindSpots": "[1-2 brutally honest sentences about potential weaknesses or what investors are ignoring/overlooking. Must reflect the MBTI type's typical blind spots.]",`,
    `  "mbtiType": "[EXACTLY ONE MBTI type: INTJ, INTP, ENTJ, ENTP, INFJ, INFP, ENFJ, ENFP, ISTJ, ISFJ, ESTJ, ESFJ, ISTP, ISFP, ESTP, or ESFP]",`,
    `  "investorType": "[1-2 sentences describing what kind of investor personality is attracted to this MBTI company type]"`,
    `}`,
    ``,
    `Return ONLY the JSON, no markdown code blocks or extra text.`,
  ].join('\n');

  try {
    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${OPENAI_API_KEY}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [
          {
            role: 'user',
            content: prompt,
          },
        ],
        temperature: 0.9,
        max_tokens: 800,
      }),
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.statusText}`);
    }

    const data = await response.json();
    let content = data.choices?.[0]?.message?.content?.trim();

    if (!content) {
      throw new Error('OpenAI returned empty personality analysis.');
    }

    if (content.startsWith('```json')) {
      content = content.replace(/```json\n?/g, '').replace(/```\n?/g, '');
    } else if (content.startsWith('```')) {
      content = content.replace(/```\n?/g, '');
    }

    const parsed = JSON.parse(content);

    try {
      await savePersonalityAnalysis({
        ticker,
        stockName,
        ...parsed
      });
    } catch (error) {
      console.warn('Failed to save personality analysis to cache:', error);
    }

    return parsed;
  } catch (error) {
    console.error('Error generating stock personality analysis:', error);
    throw error;
  }
}
